import { isEmpty, isNil } from 'lodash';
import { ControlsState, FilterSelectionState, SortOption } from '../types';

type FilterDispatchAction = {
  type: 'filter';
  operation:
    | 'addFilter'
    | 'removeFilter'
    | 'removeAllFilters'
    | 'addSelections'
    | 'updateSelections'
    | 'removeAllSelections';
  payload: Partial<FilterSelectionState>;
};

type SortDispatchAction = {
  type: 'sort';
  payload: SortOption;
};

type GroupDispatchAction = {
  type: 'group';
  groupIndex?: number;
  payload: Partial<FilterSelectionState>;
};

type ResetGroupsDispatchAction = {
  type: 'resetGroups';
};

type ResetDispatchAction = {
  type: 'reset';
  payload: ControlsState;
};

type ControlsDispatchAction =
  | FilterDispatchAction
  | SortDispatchAction
  | GroupDispatchAction
  | ResetGroupsDispatchAction
  | ResetDispatchAction;

// this state tracks what is actually sent in the query
// so if there's a UI filter but it has no options selected,
// this reducer does not add it
export function reducer(state: ControlsState, action: ControlsDispatchAction): ControlsState {
  switch (action.type) {
    case 'filter': {
      const filters = filterReducer(state.filters, action);
      return { ...state, filters };
    }

    case 'sort': {
      const sorts = sortReducer(state.sorts, action);
      return { ...state, sorts };
    }

    case 'group': {
      const groups = groupReducer(state.groups, action);
      return { ...state, groups };
    }

    case 'resetGroups': {
      return { ...state, groups: [] };
    }

    case 'reset': {
      return action.payload;
    }

    default: {
      return state;
    }
  }
}

function filterReducer(
  filters: ControlsState['filters'],
  action: FilterDispatchAction,
): ControlsState['filters'] {
  const { operation, payload } = action;
  const { filter, selection, negate } = payload;
  if (isNil(filter) || isNil(filter.uuid)) {
    return filters;
  }

  const nextFilters = new Map(filters);

  switch (operation) {
    case 'addFilter':
      if (!isNil(selection)) {
        nextFilters.set(filter.uuid, { filter, selection, negate: false });
      } else {
        nextFilters.delete(filter.uuid);
      }
      break;
    case 'removeFilter':
      nextFilters.delete(filter.uuid);
      break;
    case 'removeAllFilters':
      nextFilters.clear();
      break;
    case 'addSelections':
    case 'updateSelections':
      if (!isNil(selection)) {
        nextFilters.set(filter.uuid, { filter, selection, negate: negate ?? false });
      } else {
        nextFilters.delete(filter.uuid);
      }
      break;
    case 'removeAllSelections':
      nextFilters.delete(filter.uuid);
      break;
  }
  return nextFilters;
}

function sortReducer(
  sorts: ControlsState['sorts'],
  action: SortDispatchAction,
): ControlsState['sorts'] {
  const { payload } = action;
  if (isNil(payload.uuid)) {
    return sorts;
  }

  const nextSorts = new Map(sorts);
  if (!isNil(payload.direction)) {
    nextSorts.set(payload.uuid, { sort: payload, direction: payload.direction });
  } else {
    nextSorts.delete(payload.uuid);
  }
  return nextSorts;
}

function groupReducer(
  groups: ControlsState['groups'],
  action: GroupDispatchAction,
): ControlsState['groups'] {
  let index = action.groupIndex;
  const { payload } = action;
  const { filter, selection } = payload;
  if (isNil(filter?.uuid)) {
    return groups;
  }

  let nextGroups = [...groups];
  if (!isNil(filter) && isNil(index)) {
    const newGroup = new Map();
    nextGroups = [...nextGroups, newGroup];
    index = nextGroups.length - 1;
  }
  if (!isNil(index)) {
    if (!isNil(filter) && !isNil(selection) && !isEmpty(selection)) {
      nextGroups[index].set(filter.uuid, { filter, selection, negate: false });
    } else if (isNil(filter)) {
      nextGroups.splice(index, 1);
    } else {
      nextGroups[index]?.delete(filter.uuid);
    }
  }
  return nextGroups;
}
